import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getPendingDeletions, purgeUserData, purgeUserStorage } from '../../firebase/services';
import { BottomNav } from '../common/BottomNav';
import './AdminDeletions.css';

const AdminDeletions = () => {
  const navigate = useNavigate();
  const [deletions, setDeletions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('pending');
  const [purging, setPurging] = useState(null); // userId currently being purged
  const [confirmPurge, setConfirmPurge] = useState(null); // userId awaiting confirmation

  useEffect(() => {
    fetchDeletions();
  }, []);

  const fetchDeletions = async () => {
    try {
      const results = await getPendingDeletions();
      setDeletions(results);
      setLoading(false);
    } catch (err) {
      console.error('Error fetching deletions:', err);
      setError('Failed to load deletion requests');
      setLoading(false);
    }
  };

  const handlePurge = async (userId) => {
    setPurging(userId);
    try {
      await purgeUserData(userId);
      await purgeUserStorage(userId);
      // Remove from local state
      setDeletions(prev => prev.filter(d => d.id !== userId));
      setConfirmPurge(null);
    } catch (err) {
      console.error('Error purging user:', err);
      alert('Failed to purge user data. Check console for details.');
    }
    setPurging(null);
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const getDaysInfo = (scheduledDate) => {
    if (!scheduledDate) return { days: 0, overdue: false };
    const scheduled = scheduledDate.toDate ? scheduledDate.toDate() : new Date(scheduledDate);
    const now = new Date();
    const diffMs = scheduled - now;
    const diffDays = Math.ceil(diffMs / (1000 * 60 * 60 * 24));
    return {
      days: Math.abs(diffDays),
      overdue: diffDays <= 0,
    };
  };

  const pending = deletions.filter(d => {
    const info = getDaysInfo(d.deletionScheduledDate);
    return !info.overdue;
  });

  const overdue = deletions.filter(d => {
    const info = getDaysInfo(d.deletionScheduledDate);
    return info.overdue;
  });

  const activeList = activeTab === 'pending' ? pending : overdue;

  if (loading) {
    return <div className="admin-deletions"><div className="admin-loading">Loading deletion requests...</div></div>;
  }

  if (error) {
    return <div className="admin-deletions"><div className="admin-error">{error}</div></div>;
  }

  return (
    <div className="admin-deletions">
      <header className="deletions-header">
        <button className="back-button" onClick={() => navigate('/admin/feedback')}>
          ←
        </button>
        <div className="header-content">
          <h1>Account Deletions</h1>
          <p className="deletions-subtitle">
            {pending.length} pending · {overdue.length} ready to purge
          </p>
        </div>
      </header>

      <div className="deletions-tabs">
        <button
          className={`tab-btn ${activeTab === 'pending' ? 'active' : ''}`}
          onClick={() => setActiveTab('pending')}
        >
          Pending ({pending.length})
        </button>
        <button
          className={`tab-btn ${activeTab === 'overdue' ? 'active' : ''}`}
          onClick={() => setActiveTab('overdue')}
        >
          Ready to Purge ({overdue.length})
        </button>
      </div>

      <main className="deletions-content">
        {activeList.length === 0 ? (
          <div className="no-deletions">
            {activeTab === 'pending'
              ? 'No pending deletion requests.'
              : 'No accounts ready to purge.'}
          </div>
        ) : (
          <div className="deletions-list">
            {activeList.map((item) => {
              const daysInfo = getDaysInfo(item.deletionScheduledDate);
              return (
                <div key={item.id} className="deletion-card">
                  <div className="deletion-info">
                    <p className="deletion-email">{item.id}</p>
                    <p className="deletion-dates">
                      Requested: {formatDate(item.deletionRequestedAt)}
                    </p>
                    <p className="deletion-dates">
                      Scheduled: {formatDate(item.deletionScheduledDate)}
                    </p>
                    <p className={`deletion-status ${daysInfo.overdue ? 'overdue' : 'pending'}`}>
                      {daysInfo.overdue
                        ? `${daysInfo.days} day${daysInfo.days !== 1 ? 's' : ''} overdue`
                        : `${daysInfo.days} day${daysInfo.days !== 1 ? 's' : ''} remaining`}
                    </p>
                  </div>

                  {activeTab === 'overdue' && (
                    <div className="deletion-actions">
                      {confirmPurge === item.id ? (
                        <div className="confirm-purge">
                          <p className="confirm-text">Permanently delete all data?</p>
                          <div className="confirm-buttons">
                            <button
                              className="purge-confirm-btn"
                              onClick={() => handlePurge(item.id)}
                              disabled={purging === item.id}
                            >
                              {purging === item.id ? 'Purging...' : 'Yes, Purge'}
                            </button>
                            <button
                              className="purge-cancel-btn"
                              onClick={() => setConfirmPurge(null)}
                              disabled={purging === item.id}
                            >
                              Cancel
                            </button>
                          </div>
                        </div>
                      ) : (
                        <button
                          className="purge-btn"
                          onClick={() => setConfirmPurge(item.id)}
                        >
                          Purge Now
                        </button>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {activeTab === 'overdue' && overdue.length > 0 && (
          <p className="purge-note">
            Note: Manual purge deletes all Firestore data and Storage files. The Firebase Auth account will be removed by the automated daily purge function.
          </p>
        )}
      </main>
      <BottomNav />
    </div>
  );
};

export default AdminDeletions;
